/**
 * Theme Store
 * Manages light/dark theme with persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>()(
    persist(
        (set, get) => ({
            theme: 'dark',

            setTheme: (theme) => {
                set({ theme });
                applyTheme(theme);
            },

            toggleTheme: () => {
                const current = get().theme;
                const next = current === 'dark' ? 'light' : 'dark';
                set({ theme: next });
                applyTheme(next);
            },
        }),
        {
            name: 'tpcms-theme',
        }
    )
);

function applyTheme(theme: Theme) {
    const root = document.documentElement;

    if (theme === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.toggle('dark', systemDark);
        root.classList.toggle('light', !systemDark);
    } else {
        root.classList.toggle('dark', theme === 'dark');
        root.classList.toggle('light', theme === 'light');
    }
}

// Initialize theme on load
if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('tpcms-theme');
    if (stored) {
        try {
            const { state } = JSON.parse(stored);
            applyTheme(state.theme);
        } catch {
            applyTheme('dark');
        }
    }
}
